import { createStyles, makeStyles } from '@material-ui/core';
import { theme } from '@saesa/ui-kit-front';
import { TipoColorLineaAvanceEnum } from 'config';
import { TAMANO_ICONO_CAMPANA, TAMANO_ICONO_CHEVRON, VARIABLE_ANCHO_PROGRESO } from './outageDashboard.constants';

const OutageDashboardStyles = makeStyles(() =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      minHeight: 0,
      height: '100%',
      fontFamily: 'Neutrif Studio, sans-serif',
      flex: '1 1 auto',
      position: 'relative',
      overflow: 'hidden',
      [theme.breakpoints.down('sm')]: {
        minHeight: 0,
        height: '100%',
        flex: '1 1 auto',
        overflow: 'hidden',
      },
    },
    /**
     * Desktop sin tracking: el card no se estira; el footer queda a 64px del contenido
     */
    rootKml: {
      height: 'auto',
      flex: '0 1 auto',
      [theme.breakpoints.down('sm')]: {
        height: '100%',
        flex: '1 1 auto',
      },
    },
    /**
     * Cabecera sticky DENTRO de scrollArea: pastilla + X comparten el ancho del scrollbar
     * (si la cabecera queda fuera, en cortes futuros la X se desalineaba al aparecer el scroll).
     */
    stickyCabecera: {
      position: 'sticky',
      top: 0,
      zIndex: 2,
      flexShrink: 0,
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      alignItems: 'center',
      columnGap: 12,
      padding: '24px 37px 0 37px',
      boxSizing: 'border-box',
      backgroundColor: '#FFFFFF',
      [theme.breakpoints.down('sm')]: {
        padding: '20px 16px 12px 16px',
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
      },
    },
    /**
     * Cierre en la cabecera (celda derecha de la grilla).
     */
    botonCerrarCabecera: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 24,
      height: 24,
      minWidth: 24,
      minHeight: 24,
      padding: 0,
      margin: 0,
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      flexShrink: 0,
      gridColumn: 2,
      gridRow: 1,
    },
    iconoCerrarCabecera: {
      display: 'block',
      width: 24,
      height: 24,
    },
    scrollArea: {
      flex: '1 1 auto',
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      overflowX: 'hidden',
      overflowY: 'auto',
      WebkitOverflowScrolling: 'touch',
      /**
       * Reserva el gutter del scrollbar para que pastilla/X no “salten” al overflow.
       */
      scrollbarGutter: 'stable',
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      flex: '1 1 auto',
      /**
       * Desktop: 32px entre estado (“corte en curso”) y motivo del corte
       */
      padding: '32px 37px 24px',
      boxSizing: 'border-box',
      /**
       * Figma mobile 9063:81868 — px 16
       */
      [theme.breakpoints.down('sm')]: {
        padding: '0 16px 16px',
        flex: '0 1 auto',
      },
    },
    /**
     * Figma 9146:51404 — pb 32 + gap 32 = 64px entre contenido y footer
     */
    contentKml: {
      paddingBottom: 64,
      flex: '0 1 auto',
      [theme.breakpoints.down('sm')]: {
        paddingBottom: 16,
      },
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: 8,
      padding: 0,
      margin: 0,
      width: '100%',
      minHeight: 24,
      boxSizing: 'border-box',
      gridColumn: 1,
      gridRow: 1,
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      minWidth: 0,
    },
    statusDot: {
      width: 14,
      height: 14,
      borderRadius: '50%',
      backgroundColor: TipoColorLineaAvanceEnum.IMPREVISTO,
      flexShrink: 0,
    },
    statusDotNaranja: {
      backgroundColor: '#E08A00',
    },
    statusDotAzul: {
      backgroundColor: TipoColorLineaAvanceEnum.FUTURO,
    },
    statusLabel: {
      fontFamily: 'Neutrif Studio',
      fontWeight: 400,
      fontSize: 16,
      lineHeight: '24px',
      color: 'var(--gris-l43, #5A607F)',
      margin: 0,
    },
    body: {
      display: 'flex',
      flexDirection: 'row',
      /**
       * Estira rightCol para igualar alto de leftCol (avance + reportar)
       */
      alignItems: 'stretch',
      gap: 48,
      width: '100%',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 24,
        marginTop: 0,
      },
    },
    /**
     * Figma 9146:51384 — gap 64 desktop entre columnas KML
     */
    kmlBody: {
      gap: 64,
      [theme.breakpoints.down('sm')]: {
        gap: 24,
      },
    },
    kmlLeftCol: {
      gap: 32,
      [theme.breakpoints.down('sm')]: {
        gap: 24,
      },
    },
    kmlTerStack: {
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      width: '100%',
    },
    kmlTerLabel: {
      fontFamily: 'Neutrif Studio',
      fontWeight: 400,
      fontSize: 16,
      lineHeight: '24px',
      color: 'var(--gris-l43, #5A607F)',
      margin: 0,
    },
    kmlDisclaimer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 14,
      padding: 16,
      backgroundColor: 'var(--morado-l98, #F7F5FF)',
      borderRadius: 10,
      width: '100%',
      boxSizing: 'border-box',
    },
    kmlDisclaimerIcon: {
      width: 16,
      height: 16,
      flexShrink: 0,
      marginTop: 1,
      '--icon-primary-dark': 'var(--primario-morado, #4F1FFF)',
    },
    kmlDisclaimerText: {
      fontFamily: 'Neutrif Studio',
      fontWeight: 400,
      fontSize: 14,
      lineHeight: '18px',
      color: 'var(--gris-l3, #060709)',
      margin: 0,
      flex: 1,
    },
    leftCol: {
      display: 'flex',
      flexDirection: 'column',
      gap: 32,
      flex: 1,
      minWidth: 0,
      [theme.breakpoints.down('sm')]: {
        gap: 24,
      },
    },
    leftColFull: {
      width: '100%',
      maxWidth: '100%',
    },
    rightCol: {
      width: 320,
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      alignSelf: 'stretch',
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        alignSelf: 'stretch',
      },
    },
    terBoxSuscrito: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 12,
      /**
       * Figma 9146:50810 — mensajes suscrito
       */
      padding: '16px 20px',
      backgroundColor: '#DEFDF1',
      border: '1px solid #0ECC83',
      borderRadius: 10,
      width: '100%',
      maxWidth: '100%',
      flex: 'none',
      alignSelf: 'stretch',
      boxSizing: 'border-box',
    },
    terSuscritoCheck: {
      width: 24,
      height: 24,
      display: 'block',
      flexShrink: 0,
    },
    terSuscritoTextos: {
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      flex: 1,
      minWidth: 0,
    },
    terSuscritoTitulo: {
      /**
       * Figma OV y App/H3-SemiBold
       */
      fontFamily: 'Neutrif Studio',
      fontWeight: 600,
      fontSize: 16,
      lineHeight: '20px',
      color: 'var(--gris-l3, #060709)',
      margin: 0,
    },
    terSuscritoText: {
      /**
       * Figma OV y App/Párrafo-Regular
       */
      fontFamily: 'Neutrif Studio',
      fontWeight: 400,
      fontSize: 14,
      lineHeight: '18px',
      color: 'var(--gris-l3, #060709)',
      margin: 0,
    },
    terSuscritoEmail: {
      fontFamily: 'Neutrif Studio',
      fontWeight: 400,
      fontSize: 14,
      lineHeight: '18px',
      color: 'var(--gris-l3, #060709)',
      margin: 0,
      wordBreak: 'break-word',
    },
    terStackSuscrito: {
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      width: '100%',
    },
    /**
     * Figma 9146:50806 — caja gris con TER arriba y banner abajo (columna).
     * Clase propia (no combinar con terBox) para no perder flexDirection por orden JSS.
     */
    terBoxConSuscripcion: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      justifyContent: 'flex-start',
      gap: 16,
      padding: 24,
      backgroundColor: 'var(--gris-l96, #F3F4F7)',
      borderRadius: 20,
      width: '100%',
      boxSizing: 'border-box',
      [theme.breakpoints.down('sm')]: {
        padding: '24px 16px',
      },
    },
    terBoxConTerYSuscrito: {
      flexDirection: 'column',
      alignItems: 'stretch',
      justifyContent: 'flex-start',
      gap: 16,
    },
    terSuscritoRow: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      marginTop: 12,
      paddingTop: 12,
      borderTop: '1px solid #D5F5E7',
    },
    motivo: {
      fontFamily: 'Neutrif Studio',
      fontWeight: 700,
      fontSize: 21,
      lineHeight: '28px',
      color: 'var(--gris-l3, #060709)',
      margin: 0,
      width: '100%',
      textAlign: 'left',
      /**
       * Figma mobile Title-S SemiBold 18/26 · margen superior 12
       */
      [theme.breakpoints.down('sm')]: {
        fontWeight: 600,
        fontSize: 18,
        lineHeight: '26px',
        marginTop: 12,
      },
    },
    terBox: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 16,
      padding: 24,
      backgroundColor: 'var(--gris-l96, #F3F4F7)',
      borderRadius: 20,
      width: '100%',
      boxSizing: 'border-box',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 16,
        padding: '24px 16px',
      },
    },
    terTextoWrap: {
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      flex: 1,
    },
    terHora: {
      fontFamily: 'Neutrif Studio',
      fontWeight: 700,
      fontSize: 21,
      lineHeight: '26px',
      color: TipoColorLineaAvanceEnum.NORMALIZADO,
      margin: 0,
    },
    horaInicioAzul: {
      color: TipoColorLineaAvanceEnum.FUTURO,
    },
    terSub: {
      fontFamily: 'Neutrif Studio',
      fontWeight: 400,
      fontSize: 16,
      lineHeight: '24px',
      color: 'var(--gris-l43, #5A607F)',
      margin: 0,
    },
    terEmptyText: {
      fontFamily: 'Neutrif Studio',
      fontWeight: 400,
      fontSize: 16,
      lineHeight: '24px',
      color: 'var(--gris-l43, #5A607F)',
      margin: 0,
      maxWidth: 340,
      [theme.breakpoints.down('sm')]: {
        maxWidth: '100%',
      },
    },
    botonNotificar: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      backgroundColor: '#FFFFFF',
      border: '2px solid var(--primario-morado, #4F1FFF)',
      borderRadius: 26,
      padding: '12px 24px',
      cursor: 'pointer',
      fontFamily: 'Neutrif Studio',
      fontWeight: 600,
      fontSize: 14,
      lineHeight: '18px',
      color: 'var(--primario-morado, #4F1FFF)',
      whiteSpace: 'nowrap',
      flexShrink: 0,
      '&:hover': {
        backgroundColor: 'var(--morado-l98, #F7F5FF)',
      },
      /**
       * Figma mobile: CTA full-width bajo el texto TER
       */
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        boxSizing: 'border-box',
        padding: '14px 32px 13px',
      },
    },
    /**
     * Campana 20px Figma a la izquierda del CTA Notificar cambio
     */
    iconoNotificar: {
      width: TAMANO_ICONO_CAMPANA,
      height: TAMANO_ICONO_CAMPANA,
      display: 'block',
      flexShrink: 0,
    },
    avanceBox: {
      display: 'flex',
      flexDirection: 'column',
      gap: 24,
      padding: 24,
      backgroundColor: 'var(--rojo-l97, #FFF0F3)',
      border: `1px solid ${TipoColorLineaAvanceEnum.IMPREVISTO}`,
      borderRadius: 20,
      width: '100%',
      boxSizing: 'border-box',
      [theme.breakpoints.down('sm')]: {
        padding: '24px 16px',
      },
    },
    /**
     * Figma normalizado: caja verde
     */
    avanceBoxVerde: {
      backgroundColor: '#E8FBF3',
      border: `1px solid ${TipoColorLineaAvanceEnum.NORMALIZADO}`,
    },
    /**
     * Figma programado en curso: caja naranja
     */
    avanceBoxNaranja: {
      backgroundColor: '#FFF3E0',
      border: `1px solid ${TipoColorLineaAvanceEnum.PROGRAMADO}`,
    },
    /**
     * Figma corte futuro: caja azul, etapa Notificación (#EBF9FF / #03A9F4)
     */
    avanceBoxAzul: {
      backgroundColor: '#EBF9FF',
      border: '1px solid #03A9F4',
    },
    /**
     * Figma programado reposición: verde L93
     */
    avanceBoxVerdeProgramado: {
      backgroundColor: '#DEFDF1',
      border: '1px solid #0ECC83',
    },
    avanceTitulo: {
      fontFamily: 'Neutrif Studio',
      fontWeight: 600,
      fontSize: 18,
      lineHeight: '26px',
      color: TipoColorLineaAvanceEnum.IMPREVISTO,
      margin: 0,
    },
    avanceTituloVerde: {
      color: TipoColorLineaAvanceEnum.NORMALIZADO,
    },
    avanceTituloNaranja: {
      color: '#E08A00',
    },
    avanceTituloAzul: {
      color: TipoColorLineaAvanceEnum.FUTURO,
    },
    reportarBox: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 24,
      padding: 24,
      backgroundColor: 'var(--gris-l96, #F3F4F7)',
      borderRadius: 20,
      width: '100%',
      boxSizing: 'border-box',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 16,
        padding: '24px 16px',
      },
    },
    reportarTexto: {
      fontFamily: 'Neutrif Studio',
      fontWeight: 400,
      fontSize: 16,
      lineHeight: '20px',
      color: '#5A607F',
      margin: 0,
      flex: 1,
    },
    botonReportar: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      backgroundColor: '#FFFFFF',
      border: '2px solid var(--primario-morado, #4F1FFF)',
      borderRadius: 26,
      padding: '12px 24px',
      cursor: 'pointer',
      fontFamily: 'Neutrif Studio',
      fontWeight: 600,
      fontSize: 14,
      lineHeight: '18px',
      color: 'var(--primario-morado, #4F1FFF)',
      whiteSpace: 'nowrap',
      flexShrink: 0,
      '&:hover': {
        backgroundColor: 'var(--morado-l98, #F7F5FF)',
      },
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        boxSizing: 'border-box',
      },
    },
    iconReportar: {
      width: 18,
      height: 18,
      marginLeft: 4,
      '--icon-primary-light': 'var(--primario-morado)',
    },
    timelineRow: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      maxWidth: '100%',
      position: 'relative',
      height: 54,
    },
    /**
     * Figma 2 etapas (sin brigada): tramo entre flash y reloj
     */
    timelineRowCorto: {
      width: 220,
      maxWidth: 220,
      justifyContent: 'space-between',
      /**
       * Mobile: span full width between nodes (Tracking30V2Mobile)
       */
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        maxWidth: '100%',
      },
    },
    timelineLineTrack: {
      position: 'absolute',
      /**
       * Centro de primer nodo (40/2=20 con margen) — se alinea a centers con left/right simétricos
       */
      left: 20,
      right: 20,
      top: '50%',
      height: 3,
      backgroundColor: TipoColorLineaAvanceEnum.PENDIENTE,
      transform: 'translateY(-50%)',
      borderRadius: 2,
      zIndex: 0,
    },
    /**
     * Ajuste de pista cuando maxWidth es el tramo corto
     */
    timelineLineTrackCorto: {
      left: 20,
      right: 20,
    },
    /**
     * Progreso: left = centro del 1er nodo; width se calcula en JS
     * como % del tramo entre centros (nunca 100% del row → no se pasa del último nodo).
     */
    timelineLineProgress: {
      position: 'absolute',
      left: 20,
      top: '50%',
      height: 3,
      width: `var(${VARIABLE_ANCHO_PROGRESO})`,
      maxWidth: 'calc(100% - 40px)',
      backgroundColor: TipoColorLineaAvanceEnum.IMPREVISTO,
      transform: 'translateY(-50%)',
      borderRadius: 2,
      zIndex: 1,
      transition: 'width 0.3s ease',
    },
    timelineLineProgressCorto: {
      left: 20,
      maxWidth: 'calc(100% - 40px)',
    },
    timelineLineProgressGreen: {
      backgroundColor: TipoColorLineaAvanceEnum.NORMALIZADO,
    },
    timelineLineProgressNaranja: {
      backgroundColor: TipoColorLineaAvanceEnum.PROGRAMADO,
    },
    stepNode: {
      width: 40,
      height: 40,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2,
      flexShrink: 0,
      position: 'relative',
    },
    stepDone: {
      width: 16,
      height: 16,
      borderRadius: '50%',
      backgroundColor: TipoColorLineaAvanceEnum.IMPREVISTO,
    },
    stepDoneGreen: {
      backgroundColor: TipoColorLineaAvanceEnum.NORMALIZADO,
    },
    stepDoneNaranja: {
      backgroundColor: '#E08A00',
    },
    stepActive: {
      backgroundColor: TipoColorLineaAvanceEnum.IMPREVISTO,
    },
    stepActiveGreen: {
      backgroundColor: TipoColorLineaAvanceEnum.NORMALIZADO,
    },
    stepActiveNaranja: {
      backgroundColor: '#E08A00',
    },
    stepActiveAzul: {
      backgroundColor: TipoColorLineaAvanceEnum.FUTURO,
    },
    stepPending: {
      backgroundColor: TipoColorLineaAvanceEnum.PENDIENTE,
    },
    stepIcon: {
      width: 22,
      height: 22,
      display: 'block',
    },
    estadoActualWrap: {
      display: 'flex',
      flexDirection: 'column',
      gap: 0,
    },
    estadoHora: {
      fontFamily: 'Neutrif Studio',
      fontWeight: 600,
      fontSize: 16,
      lineHeight: '24px',
      color: 'var(--gris-l3, #060709)',
      margin: 0,
      marginBottom: 4,
    },
    estadoDesc: {
      fontFamily: 'Neutrif Studio',
      fontWeight: 400,
      fontSize: 16,
      lineHeight: '24px',
      color: 'var(--gris-l3, #060709)',
      margin: 0,
      marginBottom: 8,
    },
    /**
     * Enlace texto (patrón ModalGlobalStyles.textoLink): sin underline · gap 8 al chevron
     */
    linkDetalle: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      background: 'none',
      border: 'none',
      padding: 0,
      cursor: 'pointer',
      fontFamily: 'Neutrif Studio',
      fontWeight: 600,
      fontSize: 14,
      lineHeight: '18px',
      color: 'var(--primario-morado, #4F1FFF)',
      textDecoration: 'none',
      userSelect: 'none',
      '&:hover': {
        color: '#2700B8',
        textDecoration: 'none',
      },
      '&:focus': {
        textDecoration: 'none',
      },
      '&:focus-visible': {
        outline: '2px solid #4F1FFF',
        outlineOffset: 2,
        borderRadius: 2,
      },
    },
    iconoLinkDetalle: {
      width: TAMANO_ICONO_CHEVRON,
      height: TAMANO_ICONO_CHEVRON,
      display: 'block',
      flexShrink: 0,
    },
    detalleExpandido: {
      marginTop: 8,
    },
    chartCard: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      backgroundColor: '#FFFFFF',
      boxShadow: '0px 2px 12px rgba(9, 39, 84, 0.14)',
      borderRadius: 20,
      overflow: 'hidden',
      /**
       * Solo la card se estira; el gráfico interno mantiene layout original
       */
      height: '100%',
      flex: '1 1 auto',
      alignSelf: 'stretch',
      width: '100%',
      boxSizing: 'border-box',
      [theme.breakpoints.down('sm')]: {
        height: 'auto',
        flex: '0 1 auto',
      },
    },
    chartHeader: {
      width: '100%',
      backgroundColor: 'var(--gris-l96, #F3F4F7)',
      padding: '16px 24px',
      boxSizing: 'border-box',
      textAlign: 'center',
      flexShrink: 0,
    },
    chartHeaderText: {
      fontFamily: 'Neutrif Studio',
      fontWeight: 600,
      fontSize: 16,
      lineHeight: '20px',
      color: 'var(--gris-l3, #060709)',
      margin: 0,
      whiteSpace: 'nowrap',
      [theme.breakpoints.down('sm')]: {
        whiteSpace: 'nowrap',
      },
    },
    /**
     * Layout original del bloque gráfico (sin justify center que deforma al stretch)
     */
    chartBody: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 16,
      padding: '16px 16px 32px',
      width: '100%',
      boxSizing: 'border-box',
      [theme.breakpoints.down('sm')]: {
        /**
         * 24px entre header y número, y entre bloques del gráfico
         */
        gap: 24,
        padding: '24px 0 32px',
      },
    },
    /**
     * Card KML: solo número (sin donut), centrado y estirado
     */
    chartKmlBody: {
      flex: '1 1 auto',
      justifyContent: 'center',
      padding: '24px 16px 32px',
      [theme.breakpoints.down('sm')]: {
        padding: '24px 0 32px',
      },
    },
    sinServicioNum: {
      fontFamily: 'Neutrif Studio',
      fontWeight: 600,
      fontSize: 18,
      lineHeight: '24px',
      color: TipoColorLineaAvanceEnum.IMPREVISTO,
      margin: 0,
      textAlign: 'center',
    },
    sinServicioLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      justifyContent: 'center',
    },
    dotRojo: {
      width: 7,
      height: 7,
      borderRadius: '50%',
      backgroundColor: TipoColorLineaAvanceEnum.IMPREVISTO,
    },
    labelSecundario: {
      fontFamily: 'Neutrif Studio',
      fontWeight: 400,
      fontSize: 14,
      lineHeight: '18px',
      color: 'var(--gris-l3, #060709)',
      margin: 0,
    },
    chartRelative: {
      position: 'relative',
      width: 200,
      height: 200,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      [theme.breakpoints.down('sm')]: {
        width: 191,
        height: 189,
      },
    },
    /**
     * Disco blanco central (patrón AvisoPago / Figma original)
     */
    chartCenter: {
      position: 'absolute',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 4,
      pointerEvents: 'none',
      width: 112,
      height: 112,
      borderRadius: '50%',
      backgroundColor: '#FFFFFF',
      boxShadow: '0px 1.3061px 13.061px rgba(9, 39, 84, 0.14)',
      boxSizing: 'border-box',
    },
    recuperadosNum: {
      fontFamily: 'Neutrif Studio',
      fontWeight: 700,
      fontSize: 21,
      lineHeight: '26px',
      color: 'var(--gris-l3, #060709)',
      margin: 0,
    },
    recuperadosLabel: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      fontFamily: 'Neutrif Studio',
      fontWeight: 400,
      fontSize: 12,
      lineHeight: '16px',
      color: 'var(--gris-l3, #060709)',
    },
    rowLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
    },
    dotVerde: {
      width: 9,
      height: 9,
      borderRadius: '50%',
      backgroundColor: 'var(--verde-exito, #0ECC83)',
    },
    totalText: {
      fontFamily: 'Neutrif Studio',
      fontWeight: 400,
      fontSize: 14,
      lineHeight: '18px',
      color: 'var(--gris-l3, #060709)',
      textAlign: 'center',
      margin: 0,
    },
    totalStrong: {
      fontWeight: 600,
    },
    footer: {
      marginTop: 'auto',
      width: '100%',
      backgroundColor: 'var(--morado-l98, #F7F5FF)',
      padding: '10px 16px',
      textAlign: 'center',
      borderBottomLeftRadius: 20,
      borderBottomRightRadius: 20,
      boxSizing: 'border-box',
      flexShrink: 0,
      [theme.breakpoints.down('sm')]: {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        padding: '8px 16px',
      },
    },
    footerText: {
      fontFamily: 'Neutrif Studio',
      fontWeight: 400,
      fontSize: 12,
      lineHeight: '16px',
      color: 'var(--gris-l43, #5A607F)',
      margin: 0,
    },
  })
);

export default OutageDashboardStyles;
